/**
 * PDFExportButton Component
 * Button to export performance data as PDF
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { FileDown, Loader2, Check, AlertCircle } from 'lucide-react';
import {
  generateIndividualPDF,
  generateTeamPDF,
  generateOrgPDF,
  downloadBlob,
  generatePDFFilename,
} from '../../utils/performancePDF';
import type {
  PerformanceData,
  TeamPerformanceSummary,
  OrganizationPerformanceSummary,
  PeriodType,
} from '../../types';

export type ExportType = 'individual' | 'team' | 'organization';

export interface PDFExportButtonProps {
  type: ExportType;
  data: PerformanceData | TeamPerformanceSummary | OrganizationPerformanceSummary | null;
  periodType: PeriodType;
  userName?: string;
  /** Tenant/Organization name to display in PDF header */
  tenantName?: string;
  reportElementRef?: React.RefObject<HTMLElement>;
  disabled?: boolean;
  className?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  label?: string;
}

export const PDFExportButton: React.FC<PDFExportButtonProps> = ({
  type,
  data,
  periodType,
  userName,
  tenantName,
  reportElementRef,
  disabled = false,
  className,
  variant = 'secondary',
  size = 'md',
  label,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleExport = async () => {
    if (!data || isExporting) return;

    setIsExporting(true);
    setExportStatus('idle');

    try {
      let blob: Blob;

      switch (type) {
        case 'individual':
          blob = await generateIndividualPDF(
            data as PerformanceData,
            reportElementRef?.current,
            { tenantName }
          );
          break;
        case 'team':
          blob = await generateTeamPDF(data as TeamPerformanceSummary, { tenantName });
          break;
        case 'organization':
          blob = await generateOrgPDF(data as OrganizationPerformanceSummary, { tenantName });
          break;
        default:
          throw new Error('Invalid export type');
      }

      const filename = generatePDFFilename(type, periodType, userName);
      downloadBlob(blob, filename);

      setExportStatus('success');
      setTimeout(() => setExportStatus('idle'), 2000);
    } catch (error) {
      console.error('PDF export failed:', error);
      setExportStatus('error');
      setTimeout(() => setExportStatus('idle'), 3000);
    } finally {
      setIsExporting(false);
    }
  };

  const isDisabled = disabled || !data || isExporting;

  const buttonClasses = clsx(
    'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all',
    {
      // Variants
      'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2':
        variant === 'primary' && !isDisabled,
      'bg-stone-100 text-stone-700 hover:bg-stone-200 focus:ring-2 focus:ring-stone-500 focus:ring-offset-2':
        variant === 'secondary' && !isDisabled,
      'text-stone-600 hover:text-stone-800 hover:bg-stone-100':
        variant === 'ghost' && !isDisabled,

      // Disabled state
      'opacity-50 cursor-not-allowed': isDisabled,

      // Success state
      'bg-green-100 text-green-700': exportStatus === 'success',

      // Error state
      'bg-red-100 text-red-700': exportStatus === 'error',

      // Sizes
      'px-3 py-1.5 text-sm': size === 'sm',
      'px-4 py-2 text-sm': size === 'md',
      'px-5 py-2.5 text-base': size === 'lg',
    },
    className
  );

  const getIcon = () => {
    if (isExporting) {
      return <Loader2 className="w-4 h-4 animate-spin" />;
    }
    if (exportStatus === 'success') {
      return <Check className="w-4 h-4" />;
    }
    if (exportStatus === 'error') {
      return <AlertCircle className="w-4 h-4" />;
    }
    return <FileDown className="w-4 h-4" />;
  };

  const getLabel = () => {
    if (isExporting) return 'Exporting...';
    if (exportStatus === 'success') return 'Exported!';
    if (exportStatus === 'error') return 'Export Failed';
    return label || 'Export PDF';
  };

  return (
    <button
      onClick={handleExport}
      disabled={isDisabled}
      className={buttonClasses}
      title={isDisabled ? 'No data to export' : 'Export as PDF'}
    >
      {getIcon()}
      <span>{getLabel()}</span>
    </button>
  );
};

export default PDFExportButton;
